import { z } from 'zod';

/** Positive integer rupee amount entered into credit add/remove dialogs. */
export const creditAmountSchema = z.object({
  amount: z
    .preprocess((v) => (typeof v === 'string' ? Number(v) : v), z.number())
    .refine((n) => Number.isFinite(n) && n > 0, { message: 'Enter a positive amount' }),
});
export type CreditAmountInput = z.infer<typeof creditAmountSchema>;

/** Admin-initiated password reset (used in user list row action modal). */
export const adminPasswordResetSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Use at least 8 characters')
      .regex(/[A-Za-z]/, 'Include at least one letter')
      .regex(/[0-9]/, 'Include at least one number'),
    confirmPassword: z.string().min(1, 'Confirm the password'),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });
export type AdminPasswordResetInput = z.infer<typeof adminPasswordResetSchema>;

/** Credit history date range filter (both dates optional → unbounded query). */
export const creditsHistoryFilterSchema = z.object({
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});
export type CreditsHistoryFilterInput = z.infer<typeof creditsHistoryFilterSchema>;

/** Filter form for the main user list (SADMIN / SUB_SADMIN). */
export const userListFilterSchema = z.object({
  filterBy: z.enum(['', 'user_id', 'username', 'parent', 'parent_user']),
  filterData: z.string().default(''),
});
export type UserListFilterInput = z.infer<typeof userListFilterSchema>;

/* --------------------------------------------------------------- *
 * Shared building blocks for create / update user forms.           *
 * --------------------------------------------------------------- */

/** Reusable password rules — keep aligned with `adminPasswordResetSchema`. */
const strongPassword = z
  .string()
  .min(8, 'Use at least 8 characters')
  .max(128, 'Too long')
  .regex(/[A-Za-z]/, 'Include at least one letter')
  .regex(/[0-9]/, 'Include at least one number');

/**
 * Exactly 10 numeric digits. Mirrors the Indian mobile format the legacy
 * dialer expects everywhere else in the app. The matching <Input> in the
 * forms also strips non-digit characters as the user types so the field
 * can never hold an invalid value.
 */
const phoneNumber = z
  .string()
  .regex(/^\d{10}$/, 'Enter a valid 10-digit phone number');

const pincode = z.string().min(1, 'Pincode is required').max(10, 'Too long');

const expiryDate = z
  .string()
  .min(1, 'Pick an expiry date')
  .refine(
    (v) => {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return false;
      return d.getTime() > Date.now();
    },
    { message: 'Expiry must be in the future' },
  );

/* --------------------------------------------------------------- *
 * Sub-super-admin create — SADMIN creates an ACL-scoped admin.    *
 *                                                                  *
 * Backend: POST /api/obd/register  userType: "sub_sadmin"          *
 * Required fields per legacy CreateSubSuperAdmin form.             *
 * --------------------------------------------------------------- */

export const createSubSuperAdminSchema = z
  .object({
    username: z.string().min(1, 'Username is required').max(120, 'Too long'),
    password: strongPassword,
    confirmPassword: z.string().min(1, 'Confirm the password'),
    name: z.string().min(1, 'Name is required').max(120, 'Too long'),
    emailid: z.string().email('Enter a valid email'),
    number: phoneNumber,
    company: z.string().min(1, 'Company is required').max(120, 'Too long'),
    address: z.string().min(1, 'Address is required').max(255, 'Too long'),
    pincode: z.string().min(1, 'Pincode is required').max(10, 'Too long'),
    accountType: z.enum(['0', '1'], { errorMap: () => ({ message: 'Pick an account type' }) }),
    expiryDate,
    aclId: z
      .string()
      .min(1, 'Pick an ACL setting')
      .refine((v) => /^\d+$/.test(v), 'Pick a valid ACL'),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export type CreateSubSuperAdminInput = z.infer<typeof createSubSuperAdminSchema>;

/**
 * Update — same shape MINUS password/confirmPassword (password is reset
 * via the dedicated dialog, not the update form).
 */
export const updateSubSuperAdminSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Too long'),
  emailid: z.string().email('Enter a valid email'),
  number: phoneNumber,
  company: z.string().min(1, 'Company is required').max(120, 'Too long'),
  address: z.string().min(1, 'Address is required').max(255, 'Too long'),
  pincode: z.string().min(1, 'Pincode is required').max(10, 'Too long'),
  accountType: z.enum(['0', '1']),
  expiryDate,
  aclId: z.string().min(1, 'Pick an ACL setting'),
});
export type UpdateSubSuperAdminInput = z.infer<typeof updateSubSuperAdminSchema>;

/* --------------------------------------------------------------- *
 * Sub-user create — USER creates a SUSER under themselves.        *
 *                                                                  *
 * Backend: POST /api/obd/register  userType: "suser"               *
 * Subset of the full user form — no module/plan/group/location.   *
 * --------------------------------------------------------------- */

/**
 * Sub-user create — USER creates a SUSER under themselves.
 * Field set mirrors what the legacy CreateSubUser form sent to
 * /api/obd/register with userType: "suser".
 */
const subUserCommon = {
  name: z.string().min(1, 'Name is required').max(120),
  emailid: z.string().email('Enter a valid email'),
  number: phoneNumber,
  company: z.string().min(1, 'Company is required').max(120),
  address: z.string().min(1, 'Address is required').max(255, 'Too long'),
  pincode,
  accountType: z.enum(['0', '1']),
  expiryDate,
  planId: z.string().min(1, 'Pick a voice plan'),
  ttsPlanId: z.string().optional(),
  moduleIds: z.array(z.string()).default([]),
  ttsCredits: z.string().optional(),
  groupIds: z.array(z.string()).default([]),
  locationIds: z.array(z.string()).default([]),
} as const;

export const createSubUserSchema = z
  .object({
    username: z.string().min(1, 'Username is required').max(120),
    password: strongPassword,
    confirmPassword: z.string().min(1, 'Confirm the password'),
    ...subUserCommon,
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });
export type CreateSubUserInput = z.infer<typeof createSubUserSchema>;

export const updateSubUserSchema = z.object(subUserCommon);
export type UpdateSubUserInput = z.infer<typeof updateSubUserSchema>;

/* --------------------------------------------------------------- *
 * Full user create — admins creating an end-user or sub-admin.    *
 *                                                                  *
 * Backend: POST /api/obd/register                                  *
 * userType: 'user' | 'admin'                                       *
 * Conditional fields: module 6 requires 5, module 3 enables       *
 * accessKey/secretKey, module 7/10 enables TTS+totalVariable, etc. *
 * --------------------------------------------------------------- */

/**
 * Base shape — used to derive both create and update schemas. Kept as a
 * plain ZodObject so `.omit()` works (chaining `.refine()` wraps into
 * `ZodEffects`, which doesn't expose object utilities).
 */
const userFormBase = z.object({
  /* Step 1 — identity */
  username: z.string().min(1, 'Username is required').max(120),
  password: strongPassword,
  confirmPassword: z.string().min(1, 'Confirm the password'),
  name: z.string().min(1, 'Name is required').max(120),
  emailid: z.string().email('Enter a valid email'),
  number: phoneNumber,
  company: z.string().min(1, 'Company is required').max(120),
  address: z.string().min(1, 'Address is required').max(255, 'Too long'),
  pincode,
  /* Step 2 — type & plan */
  userType: z.enum(['user', 'admin']),
  accountType: z.enum(['0', '1']),
  planType: z.enum(['0', '1']).default('0'),
  planId: z.string().min(1, 'Pick a voice plan'),
  ttsPlanId: z.string().optional(),
  expiryDate,
  channels: z
    .string()
    .optional()
    .refine((v) => !v || /^\d+$/.test(v), 'Channels must be a whole number'),
  /* Step 3 — modules + extras driven by selected modules */
  moduleIds: z.array(z.string()).default([]),
  accessKey: z.string().optional(),
  secretKey: z.string().optional(),
  ttsCredits: z.string().optional(),
  totalVariable: z.string().optional(),
  /* Step 4 — allocations */
  groupIds: z.array(z.string()).default([]),
  locationIds: z.array(z.string()).default([]),
  languageIds: z.array(z.string()).default([]),
  /* Step 5 — campaign limits (only when userType=user) */
  campaignLimit: z.string().optional(),
  campaignHourLimit: z.string().optional(),
  /**
   * "Submission" flag (legacy `isSub`). When checked, the new user is
   * created as a submission/sub-team account. Wire value is `sub: 1 | 0`.
   * The checkbox is only rendered when the parent's own `sub` is 0 AND
   * the parent's team isn't "VSPL" — gating handled in the form.
   */
  isSub: z.boolean().default(false),
});

/** Cross-field validations shared between create and update flows. */
function applyUserCrossFieldRules<T extends z.ZodTypeAny>(schema: T): T {
  return schema
    .refine(
      (d: z.infer<T>) => {
        const has6 = d.moduleIds?.includes?.('6');
        const has5 = d.moduleIds?.includes?.('5');
        return !has6 || has5;
      },
      { message: 'Module 6 (Inbound) requires Module 5 (Dedicated CLI).', path: ['moduleIds'] },
    )
    .refine(
      (d: z.infer<T>) => {
        // TTS module enabled → at least one language must be picked.
        // The language picker lives inline under the TTS module card
        // (renderModuleExtras in CreateUserPage / UpdateUserPage).
        // Module 10 (Continuous Voice Campaign) deliberately does not
        // require a TTS plan or languages — it ships without them.
        const hasTts = d.moduleIds?.includes?.('7');
        if (!hasTts) return true;
        return Array.isArray(d.languageIds) && d.languageIds.length > 0;
      },
      {
        message: 'TTS module requires at least one language.',
        path: ['languageIds'],
      },
    ) as unknown as T;
}

export const createUserSchema = applyUserCrossFieldRules(
  userFormBase.refine((d) => d.password === d.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  }),
);
export type CreateUserInput = z.infer<typeof createUserSchema>;

/**
 * Update — same fields minus the credentials block. Password is reset
 * via the dedicated dialog, not the form.
 */
export const updateUserSchema = applyUserCrossFieldRules(
  userFormBase.omit({ username: true, password: true, confirmPassword: true }),
);
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

/* --------------------------------------------------------------- *
 * User Settings — tier-pricing config (SADMIN / SUB_SADMIN only). *
 *                                                                  *
 * Each of the 4 tiers is independently togglable. When a tier is  *
 * enabled (tierN === '2') its margin/interval/threshold fields    *
 * become required. Tier 2 / Tier 3 use start+end intervals where  *
 * start MUST be less than end.                                    *
 * --------------------------------------------------------------- */

const tierFlag = z.enum(['1', '2']);

export const updateUserSettingSchema = z
  .object({
    tier1: tierFlag,
    t1_margin: z.string().optional(),
    t1_interval: z.string().optional(),
    t1_threshold: z.string().optional(),

    tier2: tierFlag,
    t2_margin: z.string().optional(),
    t2_start_interval: z.string().optional(),
    t2_end_interval: z.string().optional(),
    t2_threshold: z.string().optional(),

    tier3: tierFlag,
    t3_margin: z.string().optional(),
    t3_start_interval: z.string().optional(),
    t3_end_interval: z.string().optional(),
    t3_threshold: z.string().optional(),

    tier4: tierFlag,
    t4_margin: z.string().optional(),
    t4_interval: z.string().optional(),
    t4_threshold: z.string().optional(),
  })
  /* Tier 1 required fields when enabled. */
  .refine((d) => d.tier1 !== '2' || Boolean(d.t1_margin), {
    message: 'Pick a margin',
    path: ['t1_margin'],
  })
  .refine((d) => d.tier1 !== '2' || Boolean(d.t1_interval), {
    message: 'Pick an interval',
    path: ['t1_interval'],
  })
  .refine((d) => d.tier1 !== '2' || Boolean(d.t1_threshold), {
    message: 'Enter a threshold',
    path: ['t1_threshold'],
  })
  /* Tier 2 required fields + start < end. */
  .refine((d) => d.tier2 !== '2' || Boolean(d.t2_margin), {
    message: 'Pick a margin',
    path: ['t2_margin'],
  })
  .refine((d) => d.tier2 !== '2' || Boolean(d.t2_start_interval), {
    message: 'Pick a start interval',
    path: ['t2_start_interval'],
  })
  .refine((d) => d.tier2 !== '2' || Boolean(d.t2_end_interval), {
    message: 'Pick an end interval',
    path: ['t2_end_interval'],
  })
  .refine((d) => d.tier2 !== '2' || Boolean(d.t2_threshold), {
    message: 'Enter a threshold',
    path: ['t2_threshold'],
  })
  .refine(
    (d) =>
      d.tier2 !== '2' ||
      !d.t2_start_interval ||
      !d.t2_end_interval ||
      Number(d.t2_start_interval) < Number(d.t2_end_interval),
    {
      message: 'Start interval must be less than end interval',
      path: ['t2_end_interval'],
    },
  )
  /* Tier 3 same shape as Tier 2. */
  .refine((d) => d.tier3 !== '2' || Boolean(d.t3_margin), {
    message: 'Pick a margin',
    path: ['t3_margin'],
  })
  .refine((d) => d.tier3 !== '2' || Boolean(d.t3_start_interval), {
    message: 'Pick a start interval',
    path: ['t3_start_interval'],
  })
  .refine((d) => d.tier3 !== '2' || Boolean(d.t3_end_interval), {
    message: 'Pick an end interval',
    path: ['t3_end_interval'],
  })
  .refine((d) => d.tier3 !== '2' || Boolean(d.t3_threshold), {
    message: 'Enter a threshold',
    path: ['t3_threshold'],
  })
  .refine(
    (d) =>
      d.tier3 !== '2' ||
      !d.t3_start_interval ||
      !d.t3_end_interval ||
      Number(d.t3_start_interval) < Number(d.t3_end_interval),
    {
      message: 'Start interval must be less than end interval',
      path: ['t3_end_interval'],
    },
  )
  /* Tier 4 required fields when enabled. */
  .refine((d) => d.tier4 !== '2' || Boolean(d.t4_margin), {
    message: 'Pick a margin',
    path: ['t4_margin'],
  })
  .refine((d) => d.tier4 !== '2' || Boolean(d.t4_interval), {
    message: 'Pick an interval',
    path: ['t4_interval'],
  })
  .refine((d) => d.tier4 !== '2' || Boolean(d.t4_threshold), {
    message: 'Enter a threshold',
    path: ['t4_threshold'],
  });

export type UpdateUserSettingInput = z.infer<typeof updateUserSettingSchema>;
